import { useState } from 'react'
import { designTokens } from '@/utils/design-tokens'
import { useShowTasks } from '@/hooks/useShowTasks'
import type { StrategySummaryMetrics } from '@/api/generated/models/StrategySummaryMetrics'
import type { StrategySummaryMonthlyItem } from '@/api/generated/models/StrategySummaryMonthlyItem'
import MonthlyBars from './MonthlyBars'
import {
  METRIC_META,
  formatNumber,
  formatRate,
  getCoverageNote,
  getHighlightMonth,
  getMetricKeys,
  getMonthlyDoneCounts,
  getMonthlyRate,
  getRate,
  getRateColor,
  getTotals,
  strategyColors,
  type MetricKey,
} from '../strategyMetrics'

interface MetricTilesProps {
  metrics: StrategySummaryMetrics
  monthly: StrategySummaryMonthlyItem[]
  schoolYear: number
  /** 차트 클릭 → 목록 열기. month 가 있으면 그 달(YYYY-MM), 없으면 학년도 전체 */
  onOpenList?: (metricKey: MetricKey, month: string | null) => void
}

const RADIUS = 52
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/** 원형 게이지. 해당 없음이면 점선 테두리 + "—" */
function RateGauge({ rate, label }: { rate: number | null; label: string }) {
  const hasValue = rate != null
  return (
    <svg
      viewBox="0 0 120 120"
      style={{ width: 92, height: 92, flexShrink: 0 }}
      role="img"
      aria-label={`${label} ${hasValue ? formatRate(rate) : '해당 없음'}`}
    >
      <circle
        cx="60"
        cy="60"
        r={RADIUS}
        fill="none"
        stroke={hasValue ? strategyColors.track : strategyColors.dashed}
        strokeWidth={hasValue ? 12 : 3}
        strokeDasharray={hasValue ? undefined : '5 6'}
      />
      {hasValue && (
        <circle
          cx="60"
          cy="60"
          r={RADIUS}
          fill="none"
          stroke={getRateColor(rate)}
          strokeWidth={12}
          strokeLinecap="round"
          strokeDasharray={`${((CIRCUMFERENCE * Math.min(Math.max(rate, 0), 100)) / 100).toFixed(1)} ${CIRCUMFERENCE.toFixed(1)}`}
          transform="rotate(-90 60 60)"
        />
      )}
      <text
        x="60"
        y="69"
        textAnchor="middle"
        style={{ fontSize: 26, fontWeight: 700, fill: strategyColors.text }}
      >
        {hasValue ? `${Math.round(rate)}%` : '—'}
      </text>
    </svg>
  )
}

const MONTHLY_RATE_LABEL: Record<MetricKey, string> = {
  task: '진행률',
  item: '진행률',
  budget: '집행률',
  performance: '달성률',
}

/** 월별 막대 아래 한 줄: 고른 달의 누적 (세부실행과제·업무 진행률은 완료 건수 포함) */
function MonthlySummary({
  monthly,
  metricKey,
  month,
  total,
}: {
  monthly: StrategySummaryMonthlyItem[]
  metricKey: MetricKey
  month: string
  total: number
}) {
  const item = monthly.find((m) => m.month === month)
  if (!item) return null
  const monthLabel = `${Number(item.month.split('-')[1])}월`
  const rate = getMonthlyRate(item, metricKey)
  const doneCounts = getMonthlyDoneCounts(item, metricKey)
  const subText = { color: strategyColors.subText }

  return (
    <span
      style={{
        fontSize: 13,
        color: strategyColors.text,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {doneCounts ? (
        <>
          <span style={subText}>{monthLabel} 완료</span>{' '}
          <strong>{formatNumber(doneCounts.inMonth ?? 0)}건</strong>
          <span style={subText}> · 누적 완료</span>{' '}
          <strong>{formatNumber(doneCounts.cumulative ?? 0)}건</strong>
          <span style={subText}> / 전체 {formatNumber(total)}건</span>
        </>
      ) : (
        <>
          <span style={subText}>
            {monthLabel}까지 누적 {MONTHLY_RATE_LABEL[metricKey]}
          </span>{' '}
          <strong>{formatRate(rate)}</strong>
        </>
      )}
    </span>
  )
}

function MetricTile({
  metricKey,
  metrics,
  monthly,
  schoolYear,
  onOpenList,
}: MetricTilesProps & { metricKey: MetricKey }) {
  const font = designTokens.typography.fontFamily.sans[0]
  // 막대를 누른 달 (지표마다 따로). 기본은 이번 달, 학년도가 끝났으면 마지막 달
  const [pickedMonth, setPickedMonth] = useState<string | null>(null)
  const lastElapsedMonth = getHighlightMonth(monthly)
  const selectedMonth =
    pickedMonth &&
    pickedMonth <= lastElapsedMonth &&
    monthly.some((m) => m.month === pickedMonth)
      ? pickedMonth
      : lastElapsedMonth
  const meta = METRIC_META[metricKey]
  const rate = getRate(metrics, metricKey)
  const hasValue = rate != null
  const { total, done } = getTotals(metrics, metricKey)
  const note = hasValue ? getCoverageNote(metrics, metricKey) : meta.naReason
  // 해당 없음 지표는 목록도 비어 있으므로 클릭을 받지 않는다
  const canOpenList = hasValue && onOpenList != null
  const handleSelectMonth = (month: string) => {
    setPickedMonth(month)
    if (canOpenList) onOpenList(metricKey, month)
  }

  return (
    <div
      style={{
        height: '100%',
        boxSizing: 'border-box',
        borderRadius: 12,
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        background: hasValue
          ? strategyColors.surface
          : strategyColors.naSurface,
        border: `1px ${hasValue ? 'solid' : 'dashed'} ${hasValue ? strategyColors.border : strategyColors.dashed}`,
        fontFamily: font,
      }}
    >
      <div
        role={canOpenList ? 'button' : undefined}
        tabIndex={canOpenList ? 0 : undefined}
        title={canOpenList ? `${meta.label} — 누르면 목록을 볼 수 있습니다` : undefined}
        onClick={canOpenList ? () => onOpenList(metricKey, null) : undefined}
        onKeyDown={
          canOpenList
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onOpenList(metricKey, null)
                }
              }
            : undefined
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          cursor: canOpenList ? 'pointer' : undefined,
        }}
      >
        <RateGauge rate={rate} label={meta.label} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: strategyColors.subText,
            }}
          >
            {meta.label}
          </span>
          <span
            style={{
              fontSize: hasValue ? 26 : 20,
              fontWeight: 700,
              color: hasValue ? strategyColors.text : strategyColors.subText,
            }}
          >
            {hasValue ? formatRate(rate) : '해당 없음'}
          </span>
          {hasValue && (
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: 13,
                color: strategyColors.text,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.6,
              }}
            >
              <span>
                <span style={{ color: strategyColors.subText }}>(전체)</span>{' '}
                {formatNumber(total)}
                {meta.unit}
              </span>
              <span>
                <span style={{ color: strategyColors.subText }}>
                  {meta.doneLabel}
                </span>{' '}
                {formatNumber(done)}
                {meta.unit}
              </span>
            </span>
          )}
          {note && (
            <span style={{ fontSize: 12, color: strategyColors.subText }}>
              {note}
            </span>
          )}
        </div>
      </div>
      <div
        style={{
          borderTop: `1px solid ${strategyColors.border}`,
          paddingTop: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: strategyColors.subText,
          }}
        >
          월별 진행현황 ({schoolYear}학년도, 누적)
          {hasValue &&
            (canOpenList
              ? ' · 막대를 누르면 그 달 목록을 볼 수 있습니다'
              : ' · 막대를 누르면 그 달 누적을 볼 수 있습니다')}
        </span>
        <MonthlyBars
          monthly={hasValue ? monthly : []}
          metricKey={metricKey}
          selectedMonth={selectedMonth}
          onSelectMonth={handleSelectMonth}
        />
        {hasValue && (
          <MonthlySummary
            monthly={monthly}
            metricKey={metricKey}
            month={selectedMonth}
            total={total}
          />
        )}
      </div>
    </div>
  )
}

/**
 * 요약 지표 (원형 게이지 + 전체/완료 수치 + 월별 누적 막대).
 * 세부실행과제 표시가 켜져 있으면 4개, 꺼져 있으면 3개 — 칸은 남은 너비를 나눠 채워
 * 개수가 바뀌거나 줄이 넘어가도 빈 공간이 생기지 않는다.
 */
export default function MetricTiles(props: MetricTilesProps) {
  const metricKeys = getMetricKeys(useShowTasks())
  return (
    <section
      aria-label="요약 지표"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}
    >
      {metricKeys.map((key) => (
        <div key={key} style={{ flex: '1 1 300px', minWidth: 0 }}>
          <MetricTile metricKey={key} {...props} />
        </div>
      ))}
    </section>
  )
}
